import Const from "../Const"
import debounce from 'debounce-promise'

export default class BaseAPI {
  constructor(gateway, basePath) {
    this.gateway = gateway
    this.basePath = basePath

    this._search = this.Search
    this.Search = debounce(this._search.bind(this), 250)
  }

  DB() {
    return this.gateway.DB[this.basePath]
  }

  invoke(url, query) {
    return this.gateway.invoke([this.basePath, url].join('/'), query)
  }

  Chunk(offset, count) {
    return this.Count().then(totalCount => {
      let finish = offset + count
      let overflow = totalCount - finish
      return this.DB()
        .where('__index')
        .between(offset, finish)
        .toArray()
        .then(res => {
          if (res.length == count) return res

          if (res.length < count) {
            if (overflow > 0 && (count - overflow) == res.length)
              return res

            return this.GetRemoteChunk(offset, count)
          }

          return res
        })
    })
  }

  GetRemoteChunk(offset, count) {
    return Promise.all([
      this.GetByIndex(offset - 1),
      this.GetByIndex(offset + count),
      this.invoke('', { startPosition: offset, count })
    ])
    .then(([previous, next, chunk]) => {
      if (!chunk.length) return chunk

      if (previous) chunk[0].__prev = previous.id
      if (next) chunk[chunk.length - 1].__next = next.id
      chunk.forEach((entry, index) => {
        entry.__index = offset + index
        let previousEntry = chunk[index - 1]
        let nextEntry = chunk[index + 1]
        if (previousEntry) entry.__prev = previousEntry.id
        if (nextEntry) entry.__next = nextEntry.id
      })

      return this.DB().bulkPut(chunk).then(added => chunk)
    })
  }

  Count() {
    let countName = `${this.gateway.region}-${this.gateway.version}-${this.basePath}-count`
    let localCount = Number(localStorage[countName])
    if (localCount && !Number.isNaN(localCount)) return Promise.resolve(localCount)
    else return this.invoke('count').then(res => {
      localStorage[countName] = res
      return res
    })
  }

  IsOfflineReady() {
    return localStorage[`${this.gateway.region}-${this.gateway.version}-${this.basePath}-offline-ready`] == 'true'
  }

  Ensure() {
    if (this.IsOfflineReady()) return true

    return this.invoke('').then(results => {
      results.sort((a, b) => {
        return a.id - b.id
      })

      let uniqueResults = Object.values(results.reduce((total, current, index) => {
        current.__index = index

        if (index > 0)
          current.__prev = results[index - 1].id
        if (index < (results.length - 1))
          current.__next = results[index + 1].id

        total[current.id] = current
        return total
      }, {}))

      localStorage[`${this.gateway.region}-${this.gateway.version}-${this.basePath}-count`] = uniqueResults.length
      let ProcessBatch = (function () {
        let chunk = results.splice(0, Math.min(1000, uniqueResults.length))
        if (!chunk.length) {
          console.log(`Done flushing ${this.basePath}`)
          localStorage[`${this.gateway.region}-${this.gateway.version}-${this.basePath}-offline-ready`] = true
          return
        }
        console.log(`Flushing ${chunk.length} of ${this.basePath}`)
        this.DB().bulkPut(chunk).then(() => {
          setTimeout(ProcessBatch, 250)
        })
      }).bind(this)

      ProcessBatch()
    })
  }

  Search(searchFor, offset, count) {
    let query = { searchFor, startPosition: offset || 0, count: count || 100 }
    return Promise.all([this.invoke('', query), this.invoke('count', query)])
      .then(([results, count]) => {
        let usableResults = results
        if (usableResults.length > 100)
          usableResults = usableResults.slice(0, 100)

        let db = this.DB()
        return Promise.all(usableResults.map(entry => db.get(entry.id)))
        .then(localResults => {
          let uniqueRemote = usableResults.reduce((total, current) => {
            total[current.id] = current
            return total
          }, {})

          let realResultsUnique = localResults.reduce((total, localEntry) => {
            total[localEntry.id] = {
              ...uniqueRemote[localEntry.id],
              ...localEntry
            }
            return total
          }, uniqueRemote)

          let realResults = Object.values(realResultsUnique)

          return this.DB().bulkPut(realResults)
            .then(updateResults => ({ results: realResults, count }))
        })
      })
  }

  Touch(id) {
    if (!id === undefined) return null

    console.log(`Touching ${this.basePath} ${id}`)
    return new Promise((res, rej) => {
        this.DB().get(id)
          .catch(err => rej(err))
          .then(dbResult => {
            if (!dbResult) return this.invoke(`${id}/name`).then(apiResult => res(apiResult))
            res(dbResult)
          })
    })
  }

  GetByIndex(index, dontExpand) {
    if (index === undefined) return null

    console.log(`Getting ${this.basePath} by index ${index}`)
    return new Promise((res, rej) => {
      this.DB().get({ __index: index })
        .catch(err => rej(err))
        .then(result => res(result))
    })
      .then(result => {
        if (!result) {
          let remoteFetch = this.invoke('', { startPosition: index, count: 1 })
          if (dontExpand) return remoteFetch

          return Promise.all([
            this.GetByIndex(index - 1, true),
            remoteFetch,
            this.GetByIndex(index + 1, true)
          ])
          .then(([ prev, current, next ]) => {
            current.__index = index
            if (prev)
              current.__prev = prev.id
            if (next)
              current.__next = next.id
          })
        }
        return result
      })
  }

  Get(id) {
    if (id === undefined) return null

    console.log(`Getting ${this.basePath} ${id}`)
    return new Promise((res, rej) => {
      this.DB().get(id).catch(err => rej(err)).then(result => res(result))
    }).then(res => {
      if (res) return this.PopulateObject(res)
      console.warn(`${this.basePath} / ${id} has null data. Trying from remote.`)

      return this.invoke(id).then(apiResult => {
        apiResult.__expanded = true
        return this.DB().put(apiResult).then(() => apiResult)
      })
    })
  }

  PopulateObject(dbResult) {
    if (dbResult === null) throw new Error('Null entity supplied, try fetching from API')
    console.log(`Got ${this.basePath}`, dbResult)
    if (dbResult && dbResult.__expanded)
      return dbResult

    return this.invoke(dbResult.id).then(apiResult => {
      let combinedResult = {
        ...dbResult,
        ...apiResult,
        __expanded: true
      }

      console.log('Expanding', dbResult, 'to', combinedResult)

      return this.DB().put(combinedResult)
        .then(putResult => combinedResult)
    }).catch(err => {
      console.error('API gave error for object, likely has no data backing it.', err, dbResult)

      dbResult.__empty = true
      dbResult.__error = err
      return dbResult
    })
  }
}